'use client'

import { useState } from "react"
import { motion, useReducedMotion } from 'motion/react'
import { Check, ChevronDown, ShoppingCart } from "lucide-react"

import { Button } from "@/components/elements/Button"
import DomainName from "@/components/domains/DomainName"
import TldResultList from "./TldResultList"
import { useDomainCart, CART_MAX_ITEMS } from "@/hooks/use-domain-cart"
import { toast } from "@/lib/toast"
import { formatPrice } from "@/lib/util"
import type { DomainSuggestion } from "@/lib/request"

/**
 * The AI suggester's answer.
 *
 * Grouped by verdict rather than labelled row by row. Eight rows each carrying
 * their own "Авах боломжтой" is the same word eight times; a heading over a
 * group says it once and lets the names themselves be what you read.
 *
 * Taken names are shown, not filtered out. They are the evidence that the
 * availability column means anything - a list where everything is free reads as
 * a list nobody checked.
 */

function Row({ suggestion, muted = false, action, expanded = false, onToggleExpand, signedIn = false, danVerified = false }: {
  suggestion: DomainSuggestion
  muted?: boolean
  action?: React.ReactNode
  expanded?: boolean
  onToggleExpand?: () => void
  signedIn?: boolean
  danVerified?: boolean
}) {
  // options[0] is always `.mn`, already this row's own headline - only the rest
  // are new here.
  const otherOptions = suggestion.options?.slice(1) ?? []
  const hasOthers = otherOptions.length > 0

  return (
    <li className="py-3.5 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <DomainName domain={suggestion.domain} className={muted ? 'text-muted-foreground' : ''} />
          { suggestion.reason ? (
            <p className="text-sm text-muted-foreground mt-0.5">{suggestion.reason}</p>
          ) : null }
          { hasOthers ? (
            // Collapsed by default. Sixty TLD rows unfolded across eight
            // suggestions would bury the names, which are the actual product
            // of AI mode - so opening the list costs one tap per suggestion
            // rather than arriving open the way search mode's list does.
            <button
              type="button"
              onClick={onToggleExpand}
              aria-expanded={expanded}
              className="mt-1.5 -ml-1 inline-flex items-center gap-1 rounded-sm px-1 py-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors motion-reduce:transition-none cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronDown className={`size-3.5 shrink-0 transition-transform motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
              { expanded ? 'Бусад өргөтгөл хаах' : `Өөр ${otherOptions.length} өргөтгөл харах` }
            </button>
          ) : null }
        </div>
        { action }
      </div>

      { hasOthers && expanded ? (
        <div className="mt-3">
          <TldResultList options={otherOptions} period={1} signedIn={signedIn} danVerified={danVerified} />
        </div>
      ) : null }
    </li>
  )
}

function Group({ title, note, children }: { title: string, note?: string, children: React.ReactNode }) {
  return (
    <section>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-gilroy font-semibold tracking-tight">{title}</h2>
        { note ? <p className="text-sm text-muted-foreground shrink-0">{note}</p> : null }
      </div>
      <ul className="mt-3 divide-y divide-border">{children}</ul>
    </section>
  )
}

export default function AiSuggestions({ suggestions, price, signedIn = false, danVerified = false }: { suggestions: DomainSuggestion[], price?: number | null, signedIn?: boolean, danVerified?: boolean }) {
  const cart = useDomainCart()
  const reduceMotion = useReducedMotion()
  // Which suggestions currently show their other-TLD list. A set of domains
  // rather than one index, because more than one row can be open at once.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const available = suggestions.filter(s => s.available === true)
  const taken = suggestions.filter(s => s.available === false)
  // Neither free nor gone: the registry lookup itself failed. Kept apart from
  // "taken" so a hiccup never reads as a verdict.
  const unknown = suggestions.filter(s => s.available === null)

  const toggleExpanded = (domain: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if(next.has(domain)) next.delete(domain)
      else next.add(domain)
      return next
    })
  }

  const addToCart = (domain: string, allowedYears: number[] | null = null) => {
    const res = cart.add(domain, 1, allowedYears)
    if(!res.ok && res.reason === 'full') return toast.error(`Сагсанд хамгийн ихдээ ${CART_MAX_ITEMS} домэйн нэр багтана`)
    toast.success('Сагсанд нэмэгдлээ')
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduceMotion ? 0.08 : 0.18, ease: 'easeOut' }}
      className="main-card p-5 sm:p-6 flex flex-col gap-7"
    >
      { available.length > 0 ? (
        // The price is stated once here rather than on every row: .mn is a flat
        // annual figure, so eight copies of it would carry no information.
        <Group title="Авах боломжтой" note={price ? `Жилийн ${formatPrice(price)}` : undefined}>
          { available.map(s => (
            <Row signedIn={signedIn} danVerified={danVerified}
              key={s.domain}
              suggestion={s}
              expanded={expanded.has(s.domain)}
              onToggleExpand={() => toggleExpanded(s.domain)}
              action={ cart.has(s.domain) ? (
                <Button tag="button" variant="outline" size="sm" className="shrink-0" isActive={false}>
                  <Check className="size-4" aria-hidden="true" />
                  Сагсанд байна
                </Button>
              ) : (
                // One year, adjustable in the cart. A term selector on every row
                // would put eight dropdowns on screen to change a default almost
                // nobody changes at this stage.
                <Button tag="button" size="sm" className="shrink-0" onClick={() => addToCart(s.domain, s.options?.[0]?.allowedYears ?? null)}>
                  <ShoppingCart className="size-4" aria-hidden="true" />
                  Сагсанд нэмэх
                </Button>
              ) }
            />
          )) }
        </Group>
      ) : null }

      { taken.length > 0 ? (
        <Group title="Бүртгэлтэй">
          { taken.map(s => (
            <Row signedIn={signedIn} danVerified={danVerified} key={s.domain} suggestion={s} muted expanded={expanded.has(s.domain)} onToggleExpand={() => toggleExpanded(s.domain)} />
          )) }
        </Group>
      ) : null }

      { unknown.length > 0 ? (
        <Group title="Шалгаж чадсангүй" note="Дахин оролдоно уу">
          { unknown.map(s => (
            <Row signedIn={signedIn} danVerified={danVerified} key={s.domain} suggestion={s} muted expanded={expanded.has(s.domain)} onToggleExpand={() => toggleExpanded(s.domain)} />
          )) }
        </Group>
      ) : null }
    </motion.div>
  )
}
